import type { StagePositions } from './api.ts'

/**
 * Where the session account's stages start (#673), kept with the account they
 * were read for.
 *
 * The positions in hand stop being the ones to draw stages with in two ways:
 * the session moves to another account without a reload (feedback review, a
 * link into another pharmacy), or a super admin saves the session account's
 * own positions on its page. Drawn with stale ones, a row's stage disagrees
 * with the server's filter it was listed by.
 */
export interface StagePositionsRead {
  readonly accountId: string
  readonly positions: StagePositions
}

/**
 * Announced on `window` by the account page when it saves an account's
 * positions, with a `StagePositionsRead` as the detail. An event rather than a
 * prop threaded through the account page, the way an expired session is told.
 */
export const STAGE_POSITIONS_SAVED_EVENT = 'rp:stage-positions-saved'

/** The positions to draw the session account's stages with, or undefined while unknown. */
export function positionsFor(
  read: StagePositionsRead | undefined,
  accountId: string | undefined,
): StagePositions | undefined {
  return read !== undefined && read.accountId === accountId ? read.positions : undefined
}

/**
 * Whether the session account's positions have to be read, again or for the
 * first time. `readFor` is the account the ones in hand were read for.
 */
export function needsPositions(
  signedIn: boolean,
  accountId: string | undefined,
  readFor: string | undefined,
): boolean {
  return signedIn && accountId !== undefined && readFor !== accountId
}

/** What is in hand after an account's positions were saved: the saved ones, if they are the session's. */
export function afterSave(
  read: StagePositionsRead | undefined,
  saved: StagePositionsRead,
  sessionAccountId: string | undefined,
): StagePositionsRead | undefined {
  return saved.accountId === sessionAccountId ? saved : read
}

/** Tells whoever holds the session's positions that an account's were saved. */
export function announceSaved(saved: StagePositionsRead): void {
  try {
    window.dispatchEvent(new CustomEvent(STAGE_POSITIONS_SAVED_EVENT, { detail: saved }))
  } catch {
    // No window in a test; nobody to tell.
  }
}
